import Controller from "@/controllers/index.js";

// #region Factories

/**
 * @summary Cria um objeto vazio de status via KPI de uma turbina eólica.
 * @returns View vazia de status de uma turbina.
 */
const EmptyFactoryWindTurbineStatus = function()
{
	return {
		generatingUnitStatus:     0,
		kpi:                      0,
		kpiUnit:                  "",
		kpiValue:                 0,
		monitoringElementId:      0,
		formattedLastKPIDateTime: "",
	};
};

/**
 * @summary Preenche e cria um objeto de status por KPI de uma turbina.
 * @param windTurbineStatus - Objeto de status por KPI de uma turbina.
 * @returns View preenchida de status de uma turbina.
 */
const FactoryWindTurbineStatus = function(windTurbineStatus = EmptyFactoryWindTurbineStatus())
{
	return {
		generatingUnitStatus:     windTurbineStatus.generatingUnitStatus,
		kpi:                      windTurbineStatus.kpi,
		kpiUnit:                  windTurbineStatus.kpiUnit,
		kpiValue:                 windTurbineStatus.kpiValue,
		monitoringElementId:      windTurbineStatus.monitoringElementId,
		formattedLastKPIDateTime: windTurbineStatus.formattedLastKPIDateTime,
	};
};

/**
 * @summary Cria objeto de status da turbina preenchido para o demo.
 * @param  monitoringElementId - Id fake da turbina.
 * @returns View preenchida de status de uma turbina mock para o demo.
 */
const FactoryMockWindTurbineStatus = function(monitoringElementId = 0)
{
	return {
		generatingUnitStatus: 1,
		kpi:                  1,
		kpiUnit:              "%",
		kpiValue:             0,
		monitoringElementId:  monitoringElementId,
	};
};

/**
 * @summary Cria um objeto vazio de status de conexão dos equipamentos de uma turbina.
 * @returns View vazia de status de conexão dos equipamentos de uma turbina.
 */
const EmptyFactoryWindTurbineConnectionStatus = function()
{
	return {
		formattedLastConnectedDateTime: "",
		isOnline:                       false,
		monitoringElementId:            0
	};
};

/**
 * @summary Preenche e cria um objeto de status de conexão dos equipamentos de uma turbina.
 * @param connectionStatus - Objeto de status de conexão dos equipamentos de uma turbina.
 * @returns view do objeto de conexão dos equipamentos.
 */
const FactoryWindTurbineConnectionStatus = function(connectionStatus = EmptyFactoryWindTurbineConnectionStatus())
{
	return {
		formattedLastConnectedDateTime: connectionStatus.formattedLastConnectedDateTime,
		isOnline:                       connectionStatus.isOnline,
		monitoringElementId:            connectionStatus.monitoringElementId
	};
};

/**
 * @summary Cria objeto de status de conexão dos equipamentos da turbina preenchida para o demo.
 * @param  monitoringElementId - Id fake da turbina.
 * @returns View preenchida de status de conexão dos equipamentos de uma turbina mock para o demo.
 */
const FactoryMockWindTurbineConnectionStatus = function(monitoringElementId = 0)
{
	return {
		formattedLastConnectedDateTime: "20/06/2022 13:16:38",
		isOnline:                       false,
		monitoringElementId:            monitoringElementId
	};
};

// #endregion

// #region API Calls

/**
 * @summary Solicita o status de um conjunto de turbinas.
 * @param monitoringElementIds Ids dos elementos de monitoramento tipo turbina.
 * @returns {[FactoryWindTurbineStatus]} Array de status das turbinas (unidade geradora).
 */
const GetList = async function(monitoringElementIds = [ 0 ])
{
	const resp = await Controller.WindTurbineStatus.GetList(monitoringElementIds);
	if (resp.status === 204) return [];

	const status = resp.data.map((s) => { return FactoryWindTurbineStatus(s); });
	return status;
};

/**
 * @summary Solicita o status do sensoriamento de uma turbina.
 * @param monitoringElementId Id do elemento de monitoramento tipo turbina.
 * @returns {array} Indicações de falha no sensoriamento.
 */
const GetTurbineSensors = async function(monitoringElementId = 0)
{
	const resp = await Controller.WindTurbineStatus.GetTurbineSensors(monitoringElementId);

	if (resp.status === 204)
		return [];

	return resp.data;
};

/**
 * @summary Obtém os status dos sensoriamentos de um conjunto de turbinas.
 * @param monitoringElementIds Ids dos elementos de monitoramento tipo turbina.
 * @returns {array} Indicações de falha no sensoriamento.
 */
const GetTurbinesSensors = async function(monitoringElementIds = [ 0 ])
{
	const resp = await Controller.WindTurbineStatus.GetTurbinesSensors(monitoringElementIds);

	if (resp.status === 204)
		return [];

	return resp.data;
};

/**
 * Obtém os status das conexões dos equipamentos de um conjunto de turbinas.
 * @param monitoringElementIds Ids dos elementos de monitoramento tipo turbina.
 * @returns {[FactoryWindTurbineConnectionStatus]} Array de indicações de desconexões.
 */
const GetTurbinesEquipments = async function(monitoringElementIds = [ 0 ])
{
	const resp = await Controller.WindTurbineStatus.GetTurbinesEquipments(monitoringElementIds);
	if (resp.status === 204) return [];

	const connections = resp.data.map((s) =>
	{
		return FactoryWindTurbineConnectionStatus(s);
	});
	return connections;
};

// #endregion

// #region Methods

/**
 * @summary Cria uma lista de status da turbina para a home do demo.
 * @returns Lista de status por KPI das turbinas mock.
 */
const CreateWindTurbinesStatusForDemo = function()
{
	const windTurbinesStatus = [];

	for (let i = 0; i < 23; i++)
	{
		const turbineId1 = -1 * (i + 1);
		const turbineId2 = -23 * (i + 1);

		const windTurbinePark1Status = FactoryMockWindTurbineStatus(turbineId1);
		const windTurbinePark2Status = FactoryMockWindTurbineStatus(turbineId2);
		windTurbinesStatus.push(windTurbinePark1Status, windTurbinePark2Status);
	}
	return windTurbinesStatus;
};

/**
 * @summary Cria uma lista de status de conexão dos equipamentos da turbina para a home do demo.
 * @returns Lista de status de conexão dos equipamentos da turbina.
 */
const CreateWindTurbinesConnectionStatusForDemo = function()
{
	const windTurbinesConnectionStatus = [];
	for (let i = 0; i < 23; i++)
	{
		const turbineId1 = -1 * (i + 1);
		const turbineId2 = -23 * (i + 1);

		const windTurbinePark1ConnectionStatus = FactoryMockWindTurbineConnectionStatus(turbineId1);
		const windTurbinePark2ConnectionStatus = FactoryMockWindTurbineConnectionStatus(turbineId2);
		windTurbinesConnectionStatus.push(windTurbinePark1ConnectionStatus, windTurbinePark2ConnectionStatus);
	}
	return windTurbinesConnectionStatus;
};

// #endregion

export default
{
	CreateWindTurbinesStatusForDemo,
	CreateWindTurbinesConnectionStatusForDemo,
	GetList,
	GetTurbinesEquipments,
	GetTurbineSensors,
	GetTurbinesSensors,
};